import React from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import classnames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: '100%',
    padding: [theme.marginBase * 5, 0, theme.marginBase * 6, 0],
    maxWidth: theme.maxContentWidth.reduced,
    borderRadius: theme.borderRadius.large,
    marginBottom: -theme.marginBase * 6,
    [theme.media.cardHomeScreen]: {
      marginBottom: 0,
      paddingBottom: theme.marginBase * 5,
      borderRadius: theme.borderRadius.large,
    },
    '& p': {
      ...theme.fonts.label,
      fontWeight: 400,
    },
    '& b': {
      ...theme.fonts.label,
      fontWeight: 700,
    },
  },
  styleWhite: {
    backgroundColor: theme.colors.white,
    border: '1px solid',
    borderColor: theme.colors.grayBorder,
    '& $title': {
      color: theme.colors.inkSecondary,
    },
    '& p': {
      color: theme.colors.inkSecondary,
    },
    '& b': {
      color: theme.colors.inkSecondary,
    },
  },
  styleGreen: {
    backgroundColor: theme.colors.green,
    '& $title': {
      color: theme.colors.inkPrimary,
    },
    '& $logoContainer': {
      backgroundColor: theme.colors.white,
    },
    '& $iconStyle': {
      color: theme.colors.greenAccessible,
    },
  },
  styleRed: {
    backgroundColor: theme.colors.red,
    '& $title': {
      color: theme.colors.white,
    },
    '& $logoContainer': {
      backgroundColor: theme.colors.redMuted,
    },
    '& $iconStyle': {
      color: theme.colors.redAccessible,
    },
  },
  stylePurple: {
    backgroundColor: theme.colors.purple,
    '& $title': {
      color: theme.colors.white,
    },
    '& $logoContainer': {
      backgroundColor: theme.colors.white,
    },
    '& $iconStyle': {
      color: theme.colors.purpleAccessible,
    },
    '& p': {
      color: theme.colors.white,
    },
    '& b': {
      color: theme.colors.white,
    },
  },
  styleInk: {
    backgroundColor: theme.colors.inkPrimary,
    [theme.media.cardHomeScreen]: {
      border: '1px solid',
      borderColor: `${theme.colors.white}80`,
    },

    '& $title': {
      color: theme.colors.white,
    },
    '& $logoContainer': {
      backgroundColor: theme.colors.white,
    },
    '& $iconStyle': {
      color: theme.colors.inkPrimary,
    },
    '& p': {
      color: theme.colors.white,
    },
    '& b': {
      color: theme.colors.white,
    },
  },
  styleWhitePurple: {
    backgroundColor: theme.colors.white,
    '& $title': {
      color: theme.colors.inkSecondary,
    },
    '& $logoContainer': {
      backgroundColor: theme.colors.purpleMuted,
    },
    '& $iconStyle': {
      color: theme.colors.purpleAccessible,
    },
  },
  titleContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.marginBase * 2,
    padding: [0, theme.marginBase * 3],
  },
  title: {
    ...theme.fonts.label,
    textTransform: 'uppercase',
  },
  iconStyle: {
    fontSize: theme.iconSize.regular,
  },
  logoContainer: {
    width: theme.marginBase * 4,
    height: theme.marginBase * 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
  },
  childrenContainer: {
    padding: [0, theme.marginBase * 3],
  },
}));

export enum StyleCardHome {
  WHITE = 'WHITE',
  GREEN = 'GREEN',
  RED = 'RED',
  PURPLE = 'PURPLE',
  WHITE_PURPLE = 'WHITE_PURPLE',
  INK = 'INK',
}

interface Props {
  title: string;
  icon?: string;
  style?: StyleCardHome;
  children?: React.ReactNode;
  className?: string;
  childrenClassName?: string;
}

export const CardHomeBase = ({
  title,
  style,
  children,
  icon,
  className,
  childrenClassName,
}: Props) => {
  const classes = useStyles();

  return (
    <div
      className={classnames(
        classes.container,
        {
          [classes.styleWhite]: style === StyleCardHome.WHITE,
          [classes.styleGreen]: style === StyleCardHome.GREEN,
          [classes.styleRed]: style === StyleCardHome.RED,
          [classes.stylePurple]: style === StyleCardHome.PURPLE,
          [classes.styleWhitePurple]: style === StyleCardHome.WHITE_PURPLE,
          [classes.styleInk]: style === StyleCardHome.INK,
        },
        className,
      )}
    >
      <div className={classes.titleContainer}>
        <h3 className={classes.title}>{title}</h3>
        {icon && (
          <div className={classes.logoContainer}>
            <i className={classnames(icon, classes.iconStyle)} title="Logo" />
          </div>
        )}
      </div>
      <div className={classnames(classes.childrenContainer, childrenClassName)}>
        {children}
      </div>
    </div>
  );
};
